import React, { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { Logo, LogoOrnament } from "@/components/Logo";
import { StatsCard } from "@/components/StatsCard";
import { CentralWheel } from "@/components/CentralWheel";
import { TapHint } from "@/components/TapHint";
import { EraCard } from "@/components/EraCard";
import { UpgradePanel } from "@/components/UpgradePanel";
import { ConfigMenu } from "@/components/ConfigMenu";
import { EraBanner } from "@/components/EraBanner";
import { DiscordInviteModal } from "@/components/DiscordInviteModal";
import { Background } from "@/components/Background";
import { BottomNav } from "@/components/BottomNav";
import { StatsView } from "@/components/StatsView";
import { AchievementsView } from "@/components/AchievementsView";
import { DonationsView } from "@/components/DonationsView";
import { SkateEntryCard } from "@/components/SkateEntryCard";
import { useGame } from "@/game/engine";
import { playEraSound, playUpgradeSound } from "@/game/audio";

// Skate Rank imports (nested app)
import { AuthProvider } from "@/skate/context/AuthContext";
import SkateProtectedRoute from "@/skate/components/ProtectedRoute";
import SkateBottomNav from "@/skate/components/BottomNav";
import SkateLanding from "@/skate/pages/Landing";
import SkateOnboarding from "@/skate/pages/Onboarding";
import SkateDashboard from "@/skate/pages/Dashboard";
import SkateLeaderboard from "@/skate/pages/Leaderboard";
import SkateChallenges from "@/skate/pages/Challenges";
import SkateCreateChallenge from "@/skate/pages/CreateChallenge";
import SkateScene from "@/skate/pages/Scene";
import SkateProfile from "@/skate/pages/Profile";
import SkatePlayerView from "@/skate/pages/PlayerView";
import { ArrowLeft } from "lucide-react";

// ---- BendyWheels Main Menu (game) ----
function BendyWheelsGame() {
  const navigate = useNavigate();
  const [eraBanner, setEraBanner] = useState(null);
  const [discordOpen, setDiscordOpen] = useState(false);
  const [activeView, setActiveView] = useState(null);
  const bannerTimerRef = useRef(null);
  const soundEnabledRef = useRef(true);
  const discordShownRef = useRef(false);

  const handleEraUnlock = useCallback((upgrade) => {
    if (upgrade.unlockDiscord) {
      if (discordShownRef.current) return;
      discordShownRef.current = true;
      setDiscordOpen(true);
      if (soundEnabledRef.current) playEraSound();
      return;
    }
    setEraBanner(upgrade);
    if (soundEnabledRef.current) playEraSound();
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setEraBanner(null), 3200);
  }, []);

  const {
    state,
    production,
    eraKey,
    clickWheel,
    buyUpgrade,
    toggleSound,
    resetGame,
  } = useGame({ onEraUnlock: handleEraUnlock });

  useEffect(() => {
    soundEnabledRef.current = state.soundEnabled;
  }, [state.soundEnabled]);

  useEffect(() => {
    discordShownRef.current = state.discordShown === true;
  }, [state.discordShown]);

  const handleBuy = (id) => {
    const ok = buyUpgrade(id);
    if (ok && state.soundEnabled) playUpgradeSound();
  };

  const handleReset = () => {
    resetGame();
    setEraBanner(null);
  };

  const handleSelectTab = (id) => {
    if (id === "mejoras") {
      setActiveView(null);
      return;
    }
    setActiveView(id);
  };

  return (
    <div
      className="min-h-screen w-full text-white overflow-x-hidden pb-16"
      style={{ backgroundColor: "#0b0b0b" }}
    >
      <Background eraKey={eraKey} />

      <main className="w-full min-h-screen flex flex-col lg:flex-row">
        <section className="w-full lg:w-3/5 flex flex-col items-center lg:h-[calc(100vh-4rem)] lg:sticky lg:top-0 px-3 sm:px-6 pt-4 sm:pt-6 pb-4 gap-4 sm:gap-6">
          <LogoOrnament />
          <Logo />

          <StatsCard
            wheelsBuilt={state.wheelsBuilt}
            berries={state.berries}
            production={production}
          />

          <div className="flex-1 w-full flex items-center justify-center py-2 sm:py-4 min-h-[280px]">
            <CentralWheel
              eraKey={eraKey}
              perClick={state.perClick}
              onClick={clickWheel}
              soundEnabled={state.soundEnabled}
            />
          </div>

          <TapHint perClick={state.perClick} />

          <EraCard upgrades={state.upgrades} seenEras={state.seenEras} />

          <SkateEntryCard onEnter={() => navigate("/skate")} />
        </section>

        <section
          className="w-full lg:w-2/5 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto border-t lg:border-t-0 lg:border-l"
          style={{ borderColor: "#1a1a1a", backgroundColor: "#0a0a0a" }}
        >
          <UpgradePanel state={state} onBuy={handleBuy} />
        </section>
      </main>

      <BottomNav activeId={activeView || "mejoras"} onSelect={handleSelectTab} />

      <StatsView
        open={activeView === "stats"}
        onClose={() => setActiveView(null)}
        state={state}
        production={production}
      />
      <AchievementsView
        open={activeView === "logros"}
        onClose={() => setActiveView(null)}
        state={state}
      />
      <DonationsView
        open={activeView === "donaciones"}
        onClose={() => setActiveView(null)}
      />
      <ConfigMenu
        open={activeView === "config"}
        onClose={() => setActiveView(null)}
        soundEnabled={state.soundEnabled}
        onToggleSound={toggleSound}
        onReset={handleReset}
      />

      <EraBanner upgrade={eraBanner} />
      <DiscordInviteModal open={discordOpen} onClose={() => setDiscordOpen(false)} />
    </div>
  );
}

// ---- Back button used on Skate landing/onboarding (routes without SkateShell) ----
const BackToMenuButton = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/")}
      aria-label="Back to main menu"
      data-testid="skate-back-to-menu"
      className="fixed top-3 right-3 sm:top-4 sm:right-4 z-30 flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-black/85 border border-zinc-800 hover:border-[#FACC15] transition-colors backdrop-blur-md"
    >
      <ArrowLeft className="w-3.5 h-3.5 text-white" strokeWidth={2} />
      <span className="font-mono text-[8px] sm:text-[9px] tracking-[0.2em] sm:tracking-[0.25em] text-zinc-300">
        BENDYWHEELS
      </span>
    </button>
  );
};

// ---- Skate Shell (wraps skate pages with bottom nav + back button) ----
const SkateShell = ({ children, showNav = true }) => {
  const navigate = useNavigate();
  return (
    <div className="skate-shell min-h-screen relative noise pb-24" data-testid="skate-shell">
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label="Back to main menu"
        data-testid="skate-back-to-menu"
        className="fixed top-3 right-3 sm:top-4 sm:right-4 z-30 flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-black/85 border border-zinc-800 hover:border-[#FACC15] transition-colors backdrop-blur-md"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
        <span className="font-mono text-[8px] sm:text-[9px] tracking-[0.2em] sm:tracking-[0.25em] text-zinc-300">
          BENDYWHEELS
        </span>
      </button>
      <div className="max-w-2xl mx-auto px-5 pt-6 relative z-10">{children}</div>
      {showNav && <SkateBottomNav />}
    </div>
  );
};

// ---- Skate Routes ----
const SkateRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={
        <div className="skate-shell noise relative">
          <BackToMenuButton />
          <SkateLanding />
        </div>
      } />
      <Route
        path="/onboarding"
        element={
          <SkateProtectedRoute requireOnboarded={false}>
            <div className="skate-shell noise relative">
              <BackToMenuButton />
              <SkateOnboarding />
            </div>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateDashboard /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateLeaderboard /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/challenges"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateChallenges /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/challenges/new"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateCreateChallenge /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/challenges/new/:opponentId"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateCreateChallenge /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/scene"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateScene /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkateProfile /></SkateShell>
          </SkateProtectedRoute>
        }
      />
      <Route
        path="/skater/:userId"
        element={
          <SkateProtectedRoute>
            <SkateShell><SkatePlayerView /></SkateShell>
          </SkateProtectedRoute>
        }
      />
    </Routes>
  );
};

// ---- Top-level app routes ----
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<BendyWheelsGame />} />
      <Route path="/skate/*" element={<SkateRoutes />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#0d0d0d",
            border: "1px solid #FFD400",
            color: "#fff",
            fontFamily: "Orbitron, sans-serif",
            fontSize: "12px",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            borderRadius: 2,
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
